import { ref } from "vue";
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import type { Campaign } from "~/types/campaign";
import { LOCALE_INTL, type Langue } from "~/utils/langue";

/**
 * Charge la campagne en SSR et configure tous les meta tags SEO/OG
 * pour la page de paiement /c/[slug]/payment.
 *
 * Extrait de `pages/c/[slug]/payment.vue` lors du split anti-800l.
 *
 * NB SSR Nuxt 4 : après un `await` dans un composable exporté, le
 * contexte Nuxt est perdu. Les appels suivants à `useSeoMeta` / `useHead`
 * (qui dépendent en interne de `useNuxtApp()`) doivent être faits dans
 * `nuxtApp.runWithContext(() => ...)`, sinon erreur SSR
 * `[nuxt] instance unavailable` (HTTP 500 sur la page).
 */
export async function useCampaignPaymentSeo(campaignSlug: string) {
  const nuxtApp = useNuxtApp();
  const config = useRuntimeConfig();
  // Origine du site COURANT (prod/staging/préview) pour le fallback
  // og-image — cf. useShareBaseUrl. IMPORTANT : capturé AVANT le await
  // (le contexte Nuxt est perdu après, cf. NB SSR ci-dessus).
  const shareBase = useShareBaseUrl();
  const { t, locale } = useI18n();
  const ogLocale = (LOCALE_INTL[locale.value as Langue] ?? "fr-FR").replace("-", "_");

  // ============================================
  // SSR: Charger les données pour les meta tags OG
  // ============================================
  const { data: ssrCampaignData } = await useFetch<{ data: Campaign }>(
    `${config.public.apiUrl}/api/campaigns/public/slug/${campaignSlug}`,
    {
      key: `campaign-payment-ssr-${campaignSlug}`,
      server: true,
      lazy: false,
      // Retry transitoire (timeout/5xx) — éviter le flaky SSR (Karim 2026-06-21).
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  ).catch(() => ({ data: ref(null) }));

  // stripHtml importé depuis @donoor/utils/string (source unique cross-app).

  // Construire l'URL absolue de l'image pour les meta tags OG
  // Utilise Directus directement car les assets sont publics (pas de redirection login).
  // Transformations Directus : 1200x630 + format JPG (Telegram/Twitter ne lisent pas SVG,
  // et les PNG bruts > 1MB peuvent timeout sur le crawler Telegram).
  // Host Directus depuis la CONFIG (jamais en dur) : en staging, l'asset
  // n'existe que sur directus-staging → l'URL prod en dur cassait l'image
  // de partage (bug Karim 2026-07-02).
  const getAbsoluteImageUrl = (
    imageId: string | null | undefined,
  ): string => {
    if (!imageId) return `${shareBase}/og-image.png`;
    if (imageId.startsWith("http")) return imageId;
    return `${config.public.apiUrl}/api/assets/${imageId}?width=1200&height=630&fit=cover&format=jpg&quality=85`;
  };

  // SEO - Metadonnées SSR (disponibles immédiatement pour les crawlers)
  const ssrCampaign = ssrCampaignData.value?.data;
  const ogImageUrl = getAbsoluteImageUrl(ssrCampaign?.image);
  const cleanDescription = stripHtml(ssrCampaign?.description);
  const ogDescription = cleanDescription
    ? truncateForMeta(t("campagne.paiementSeo.ogDescriptionAvecDescription", { titre: ssrCampaign?.title, description: cleanDescription }), 160)
    : ssrCampaign?.title
      ? t("campagne.paiementSeo.ogDescriptionTitreSeul", { titre: ssrCampaign.title })
      : t("campagne.paiementSeo.ogDescriptionParDefaut");
  const ogTitle = ssrCampaign?.title
    ? t("campagne.paiementSeo.ogTitreAvecNom", { titre: ssrCampaign.title })
    : t("campagne.paiementSeo.ogTitreParDefaut");
  const canonicalUrl = `https://donoor.org/c/${campaignSlug}/payment`;

  // Wrap les composables Nuxt post-await dans runWithContext pour restaurer
  // l'instance Nuxt perdue par le `await useFetch` ci-dessus.
  nuxtApp.runWithContext(() => {
    useSeoMeta({
      title: ssrCampaign?.title
        ? t("campagne.paiementSeo.ogTitreAvecNom", { titre: ssrCampaign.title })
        : t("campagne.paiementSeo.titrePageParDefaut"),
      description: ogDescription,
      // Page de paiement — la cagnotte parente /c/[slug] est indexable, cette
      // étape de checkout ne doit pas l'être (canonical pointerait vers la cagnotte).
      robots: "noindex, nofollow",
      ogType: "website",
      ogTitle,
      ogDescription,
      ogImage: ogImageUrl,
      ogImageWidth: 1200,
      ogImageHeight: 630,
      ogImageAlt: ssrCampaign?.title
        ? t("campagne.paiementSeo.ogImageAltAvecNom", { titre: ssrCampaign.title })
        : t("campagne.paiementSeo.ogImageAltParDefaut"),
      ogUrl: canonicalUrl,
      ogSiteName: "Donoor",
      ogLocale,
      twitterCard: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: ogImageUrl,
      twitterSite: "@donoor_org",
    });

    useHead({
      link: [{ rel: "canonical", href: canonicalUrl }],
    });
  });

  return { ssrCampaign };
}
